import React, { FC, useCallback, useMemo } from 'react';

import { LicenseInfoTypeFragment, LicensePaymentState, LicenseType } from 'domain/billing/types';

import LicensesListItem from '../LicensesListItem';
import { LicensesModalAction } from '../../types';

import styles from './index.module.scss';

export type ModalLicenseUnpaidType = LicenseInfoTypeFragment & {
  isChecked: boolean;
};

interface LicensesUnpaidListProps {
  unpaidLicenses: LicenseType[];
  toEnableAutorenewal: string[];
  dispatch: (value: LicensesModalAction) => void;
  setSubscriptionCost: (licensesIds: string[]) => Promise<void>;
}

const LicensesUnpaidList: FC<LicensesUnpaidListProps> = ({
  unpaidLicenses,
  toEnableAutorenewal,
  dispatch,
  setSubscriptionCost,
}) => {
  const getModalUnpaidLicenseItem = useCallback(
    (item: LicenseInfoTypeFragment): ModalLicenseUnpaidType => {
      const isInEnableAutorenewal = toEnableAutorenewal.indexOf(item.id) > -1;
      const isChecked = isInEnableAutorenewal || item.autoRenewal;

      return {
        ...item,
        isChecked,
      };
    },
    [toEnableAutorenewal]
  );

  const licenses = useMemo(
    () => unpaidLicenses?.map(getModalUnpaidLicenseItem),
    [unpaidLicenses, getModalUnpaidLicenseItem]
  );

  const onChange = useCallback(
    (item: LicenseInfoTypeFragment, value: boolean) => {
      if (value) {
        const currentToEnableAutorenewal = [...toEnableAutorenewal, item.id];
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: currentToEnableAutorenewal });
        setSubscriptionCost(currentToEnableAutorenewal);
      } else {
        const newToEnableAutorenewal = toEnableAutorenewal.filter((id) => id !== item.id);
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: newToEnableAutorenewal });
        setSubscriptionCost(newToEnableAutorenewal);
      }
    },
    [toEnableAutorenewal]
  );

  return (
    <div className={styles.licensesUnpaidList}>
      {licenses?.map((item) => (
        <LicensesListItem
          item={item}
          key={`licenses_unpaid_list_item_${item.id}`}
          isChecked={item.isChecked}
          isGraceSubtitleVisible={item.paymentState === LicensePaymentState.Grace}
          isWaitPaymentSubtitleVisible={item.paymentState === LicensePaymentState.WaitPayment}
          onChange={(value) => onChange(item, value)}
        />
      ))}
    </div>
  );
};

export default React.memo(LicensesUnpaidList);
